import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {useForm, useFormState} from 'react-final-form';
import {Card, FormLabel, Grid} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Dispatch, SelectOption, Store, StoreResourceData} from 'spectra-logic-ui';
import {createCollection, fetchResource} from 'spectra-logic-ui/actions';
import {Checkbox, MultiSelect, SingleSelect, TextField, Tooltip, WizardPage} from 'spectra-logic-ui/components';

import {FormActionType, StorageClass, TargetType} from '@/enum';
import {Bucket, CloudProperties, Pool, Target, TargetItem, TargetItemDetails} from '@/types';
import DialogDescription from '@/components/form/dialog_description';
import FormForwardSelect from '@/components/form/forward_select';
import Radio from '@/components/radio';
import {isArchivalSupported, isStorageClassArchival} from '@/storage/archive';
import {objectRestoreTooltip, recoverableTooltip} from '@/storage/details/properties';
import {itemDetails, items} from '@/storage/form/store';
import DisabledCard from '@/storage/form/pages/disabled_card';
import {AddressingStyleTooltip, AwsLinkTooltip} from '@/storage/form/pages/cloud/aws/tooltips';
import {UseWithTooltip} from '@/storage/form/pages/tooltips';

type ParametersCardProps = {
  action: FormActionType;
  target?: TargetType;
  targetTypes?: Target[];
  pool?: Pool;
  buckets: Bucket[];
  bucketsFetching: boolean;
  targetItems?: TargetItem[];
  exporter: boolean;
  currentOnly: boolean;
  theirClass: string;
  storageClass: StorageClass;
  itemDetails?: TargetItemDetails;
  itemFetching: boolean;
  error?: string;
  useWith?: string[];
  importer?: boolean;
}

type PageProps = {
  title: string;
  action: FormActionType;
  buckets?: Bucket[];
  bucketsFetching?: boolean;
  targetTypes?: Target[];
  targetItems?: TargetItem[];
  fetchBuckets?: Function;
  fetchCreateStorageItemDetails?: Function;
  fetchTargetTypes?: Function;
  pool?: Pool;
  itemDetails?: TargetItemDetails;
  itemFetching?: boolean;
  itemErrorMessage?: string;
  validate?: (values: any) => object;
}

const useParametersCardStyles = makeStyles({
  root: {
    padding: 15,
    marginTop: 10,
  },
  grid: {
    '& > *': {
      marginBottom: 10,
    },
  },
  addressingStyle: {
    display: 'inline-block',
  },
});

const ParametersCard = ({buckets = [], targetItems = [], action,
  target, targetTypes, pool, exporter, currentOnly, storageClass, itemDetails,
  itemFetching, bucketsFetching, error='', useWith = [], importer=false}: ParametersCardProps) => {
  const classes = useParametersCardStyles();
  const targetInfo = targetTypes ? targetTypes.find((t) => t.id === target) : undefined;
  const cloudBucketTooltip = targetInfo && targetInfo.tip ? targetInfo.tip : undefined;
  const exportTooltip = 'When enabled, the system will export objects to the cloud bucket instead of adding '+
    'storage to the object.';
  const currentOnlyTooltip = 'When enabled, the storage can only be used with current object versions. '+
    'This is used to allow the use of cloud storage that doesn\'t support object versions.';
  const destClassLabel = target === TargetType.AZURE ? 'Azure '+
    'Tier' : target === TargetType.GOOGLE ? 'Google Class' : 'Destination Class';
  const destClassTip = 'The selected '+destClassLabel+' will be specified when uploading content to the '+
    'cloud storage. If the default is used, content will be uploaded without specifying a value. The resulting '+
    'charges are controlled by the cloud provider.';
  const bucketDesc = targetInfo ? targetInfo.name : 'Cloud Bucket';
  const cloudBucketSelectorLabel = action === FormActionType.EDIT ? bucketDesc : 'Select '+bucketDesc;
  const bucketOptions = buckets.map((b) => {
    return {key: b.name, text: b.name};
  }) as SelectOption[];
  let cloudBucketOptions;
  if (action === FormActionType.EDIT && pool && pool.item) {
    cloudBucketOptions = [{key: pool.item, text: pool.item}] as SelectOption[];
  } else {
    cloudBucketOptions = targetItems.map((b) => ({key: b.id, text: b.name ? b.name : b.id})) as SelectOption[];
  }

  const form = useForm();
  const disableRestoreForNonArchival = (e: any) => {
    if (e && e.target && !isStorageClassArchival(e.target.value)) {
      form.mutators.setFormValue('restoreInPlace', false);
    }
  };
  const currentOnlyAllowed = target === TargetType.S3 || target === TargetType.S3OTHER;
  const disableOnEdit = action === FormActionType.EDIT;
  const disableExportDependentFields = (e: any) => {
    if (e && e.target && e.target.checked) {
      if (itemDetails && itemDetails.link) {
        form.mutators.setFormValue('importer', false);
      }
      form.mutators.setFormValue('restoreInPlace', false);
      form.mutators.setFormValue('recoverable', true);
    } else {
      form.mutators.setFormValue('recoverable', false);
    }
  };
  const disableCurrentOnlyDependentFields = (e: any) => {
    if (e && e.target && e.target.checked) {
      if (itemDetails && itemDetails.link) {
        form.mutators.setFormValue('importer', false);
      }
    }
  };

  const recoverableEnabled = !importer;

  const updateImportOptions = (e: any) => {
    if (e && e.target && (target === TargetType.S3 || target === TargetType.S3OTHER)) {
      const value = e.target.value;
      const importEnabled = value && value.length == 1 && itemDetails && itemDetails.link;
      if (importEnabled && target === TargetType.S3OTHER) {
        // S3 Other doesn't support notifications. Thus they must always be paused
        // when creating linked storage.
        form.mutators.setFormValue('pauseNotifications', true);
      }
      if (!importEnabled) {
        form.mutators.setFormValue('importer', false);
        form.mutators.setFormValue('pauseNotifications', false);
      }
    }
  };

  const updatePauseNotifications = (e: any) => {
    if (!e || !e.target || !e.target.checked) {
      form.mutators.setFormValue('pauseNotifications', false);
    } else if (target === TargetType.S3OTHER) {
      // Non-AWS S3 endpoints usually don't support notifications, so default to
      // pausing notifications.
      form.mutators.setFormValue('pauseNotifications', true);
    }
  };

  const clearStorageClass = () => {
    form.mutators.setFormValue('storageClass', undefined);
  };
  const stgClasses = itemDetails && itemDetails.classes ? itemDetails.classes : [];
  let cloud = {} as CloudProperties;
  cloud = itemDetails ? itemDetails.properties as CloudProperties : {} as CloudProperties;
  const theirClasses = cloud.theirClasses ? cloud.theirClasses : [];

  return (
    <Card className={classes.root}>
      {error && <p className='error'>{error}</p>}
      <Grid container spacing={2} className={classes.grid}>
        <Grid item xs={6}>
          <FormForwardSelect
            name='item' target='name' label={cloudBucketSelectorLabel} tooltip={cloudBucketTooltip}
            options={cloudBucketOptions} disabled={action === FormActionType.EDIT}
            onChange={clearStorageClass}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField name='name' label='Storage Name' />
        </Grid>
        <Grid item xs={6}>
          <SingleSelect
            name='storageClass' label='Select Storage Class'
            options={stgClasses} disabled={stgClasses.length === 0 || itemFetching}
            onChange={disableRestoreForNonArchival}
          />
        </Grid>
        <Grid item xs={6}>
          <SingleSelect
            name='theirClass' label={destClassLabel} tooltip={destClassTip} noneOption='Use Default'
            options={theirClasses} disabled={theirClasses.length === 0 || itemFetching}
          />
        </Grid>
        <Grid item xs={12}>
          <MultiSelect
            name='useWith' label='Use With Buckets' tooltip={<UseWithTooltip />}
            noneOption='All buckets' disabled={bucketsFetching}
            options={bucketOptions} onChange={updateImportOptions}
          />
        </Grid>
        {itemDetails && itemDetails.link &&
          <>
            <Grid item xs={6}>
              <Checkbox
                name='importer'
                label='Import Content'
                disabled={!bucketOptions || !useWith || useWith.length != 1 || exporter || currentOnly}
                onChange={updatePauseNotifications}
                tooltip={<AwsLinkTooltip />}
              />
            </Grid>
            <Grid item xs={6}>
              <Checkbox
                name='pauseNotifications'
                label='Pause Notifications'
                disabled={!importer || (target !== TargetType.S3 && target !== TargetType.S3OTHER)}
              />
            </Grid>
          </>
        }
        <Grid item xs={6}>
          <Checkbox
            name='exporter' label='Export Objects' tooltip={exportTooltip}
            disabled={disableOnEdit}
            onChange={disableExportDependentFields}
          />
        </Grid>
        {currentOnlyAllowed &&
          <Grid item xs={6}>
            <Checkbox
              name='currentOnly' label='Current Version Only' tooltip={currentOnlyTooltip}
              disabled={disableOnEdit}
              onChange={disableCurrentOnlyDependentFields}
            />
          </Grid>
        }
        <Grid item xs={6}>
          <Checkbox
            name='recoverable' label='Third-party Recovery' tooltip={recoverableTooltip}
            disabled={!recoverableEnabled || exporter}
          />
        </Grid>
        {isArchivalSupported(stgClasses) &&
          <Grid item xs={6}>
            <Checkbox
              name='restoreInPlace' label='Restore In-place' tooltip={objectRestoreTooltip}
              disabled={!isStorageClassArchival(storageClass) || exporter}
            />
          </Grid>
        }
        {target === TargetType.S3OTHER &&
          <Grid item xs={12}>
            <FormLabel>Addressing Style</FormLabel><Tooltip><AddressingStyleTooltip /></Tooltip>
            <Radio className={classes.addressingStyle}>
              <Radio.Button name='virtualHost' label='Path' value='false' />
              <Radio.Button name='virtualHost' label='Virtual Hosted' value='true' />
            </Radio>
          </Grid>
        }
      </Grid>
    </Card>
  );
};


const CloudParametersPage = (props: PageProps) => {
  const {
    title, buckets = [], targetTypes = [], targetItems, fetchBuckets, bucketsFetching = false,
    action, pool, fetchCreateStorageItemDetails, fetchTargetTypes,
    itemDetails, itemFetching=false, itemErrorMessage, ...otherProps
  } = props;

  const form = useForm();
  const formState = useFormState();
  const item = formState.values.item;
  const target = formState.values.target;
  const exporter = formState.values.exporter;
  const currentOnly = formState.values.currentOnly;
  const storageClass = formState.values.storageClass;
  const theirClass = formState.values.theirClass;
  const importer = formState.values.importer;
  const useWith = formState.values.useWith;
  const stgClasses = itemDetails && itemDetails.classes ? itemDetails.classes : [];
  let cloud = {} as CloudProperties;
  cloud = itemDetails ? itemDetails.properties as CloudProperties : {} as CloudProperties;
  const theirClasses = cloud.theirClasses ? cloud.theirClasses : [];

  useEffect(() => {
    if (fetchBuckets !== undefined) {
      fetchBuckets();
    }
  }, []);

  useEffect(() => {
    if (fetchTargetTypes !== undefined) {
      fetchTargetTypes();
    }
  }, []);

  if (action === FormActionType.CREATE && fetchCreateStorageItemDetails) {
    useEffect(() => {
      if (item) {
        // The top-level edit storage page takes care of fetching item details
        // when editing storage. We only need this when creating storage.
        fetchCreateStorageItemDetails(formState.values);
      }
    }, [item]);

    useEffect(() => {
      if (storageClass && !stgClasses.includes(storageClass)) {
        form.mutators.setFormValue('storageClass', undefined);
      }
      if (theirClass) {
        if (!theirClasses.includes(theirClass)) {
          form.mutators.setFormValue('theirClass', undefined);
        } else {
          form.mutators.setFormValue('theirClass', theirClass);
        }
      }
    }, [itemDetails]);
  }

  useEffect(() => {
    if (importer) {
      form.mutators.setFormValue('recoverable', true);
    } else if (!exporter) {
      form.mutators.setFormValue('recoverable', false);
    }
  }, [importer]);

  return (
    <WizardPage
      title={title} {...otherProps}
      description={<DialogDescription>Provide additional information for this cloud storage below.</DialogDescription>}
    >
      <ParametersCard
        buckets={buckets}
        bucketsFetching={bucketsFetching}
        targetItems={targetItems}
        target={target}
        targetTypes={targetTypes}
        action={action}
        pool={pool}
        theirClass={theirClass}
        storageClass={storageClass}
        exporter={exporter}
        currentOnly={currentOnly}
        itemDetails={itemDetails}
        itemFetching={itemFetching}
        error={itemErrorMessage}
        useWith={useWith}
        importer={importer}
      />
      <DisabledCard />
    </WizardPage>
  );
};

const mapStateToProps = (state: Store) => {
  const bucketsResource = state.resources['buckets'] || {} as StoreResourceData<string[]>;
  const targetItemsResource = state.resources[items] || {};
  const itemState = state.resources[itemDetails] || {};
  const targetTypesResource = state.resources['targets'] || {};
  return {
    buckets: bucketsResource.data,
    bucketsFetching: bucketsResource.fetching,
    targetItems: targetItemsResource.data,
    itemDetails: itemState.data,
    itemFetching: itemState.fetching,
    itemErrorMessage: itemState.errorMessage,
    targetTypes: targetTypesResource.data,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchBuckets: () => dispatch(fetchResource('buckets')),
  fetchCreateStorageItemDetails: (values: any) => {
    const opts = {storeName: itemDetails};
    const body = getCloudBucketsBody(values);
    return dispatch(createCollection(`targets/0/${values.target}/${values.item}`, body, opts));
  },
  fetchTargetTypes: () => dispatch(fetchResource('targets')),
});

export const getCloudBucketsBody = (values: any) => {
  const target = values.target;
  const body = {} as any;
  if (values.theirClass) {
    body.theirClass = values.theirClass;
  }
  if (target === TargetType.S3) {
    if (!!values.arn) {
      body.arn = values.arn;
    }
    if (!!values.externalid) {
      body.externalid = values.externalid;
    }
  } else if (target === TargetType.S3OTHER) {
    body.endpoint = values.url;
    body.unsafe = values.unsafe;
    // Radio buttons are used instead of a single virtual host checkbox.
    // Thus, the value needs to be converted from a string to a boolean.
    body.virtualHost = values.virtualHost === 'true';
  }
  if (target === TargetType.S3 || target == TargetType.S3OTHER) {
    if (!!values.region) {
      body.region = values.region;
    }
    if (!!values.accessKey) {
      body.accessKey = values.accessKey;
    }
    if (!!values.secretKey) {
      body.secretKey = values.secretKey;
    }
  } else if (target === TargetType.GOOGLE) {
    body.credentials = values.credentials;
  } else if (target === TargetType.AZURE) {
    body.account = values.account;
    body.secret = values.secret;
  }
  return body;
};

export default connect(mapStateToProps, mapDispatchToProps)(CloudParametersPage);
